import React, { useCallback, useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import RepeatIcon from '@mui/icons-material/Repeat';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SearchIcon from '@mui/icons-material/Search';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { raffleApi } from './raffleApi';
import { downloadReceipt } from './rafflePdf';
import SessionReportPdf from '../global/SessionReportPdf';
import {
  Avatar,
  MachineTile,
  MoneyInput,
  Notice,
  SectionLabel,
  Sheet,
  SheetTitle,
  Spinner,
  TicketStub,
} from './RafflePieces';
import {
  AMBER,
  GREEN,
  RED,
  SLATE,
  formatPhone,
  fullName,
  money,
  pad,
} from './raffleTokens';

/**
 * History — §7. One scope for everybody: your own raffles from your own clock
 * session (the Ticket Out scope — the owner's call, narrower than spec R47).
 *
 * There is no location-wide view and no date range. A raffle belongs to the
 * shift it was paid on and that is how it is reviewed; the location-wide
 * picture lives in Winners Control and the Employee Session reports, which
 * are built for it. Raffle.jsx hides the History button entirely for anyone
 * with no clock session, so this never opens onto a list that cannot fill.
 *
 * Download exports the list (R50); the single-raffle receipt lives on the
 * raffle's own sheet.
 */
export default function HistoryView({
  boot,
  locationId,
  locationName,
  viewOnly,
  notify,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  // Bumping this fires SessionReportPdf, the same driver Ticket Out, Bonus
  // and Transaction use: the shared PdfBody header (session, employee,
  // shift, assigned day) rendered off-screen, then a full-screen preview
  // with download, print and zoom.
  const [reportTrigger, setReportTrigger] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await raffleApi.history(locationId);
    setLoading(false);
    if (!res.success) return notify('error', res.message);
    setData(res);
  }, [locationId, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const updateItem = (raffle) => {
    setData((d) => ({
      ...d,
      items: d.items.map((h) =>
        h.raffleId === raffle.raffleId ? { ...h, ...raffle } : h
      ),
    }));
  };

  // `detail` holds the item key, so the open sheet always shows the latest
  // copy (e.g. the Edited stamp right after a save).
  const openItem = detail
    ? (data?.items || []).find((h) => h.key === detail)
    : null;
  const totals = data?.totals || {
    paidAmount: 0,
    paidCount: 0,
    unclaimedCount: 0,
  };
  // Named by the day it ran, not by its clock times: "This shift · Aug 2"
  // reads as a heading, where "9:14 AM – open" reads as a detail.
  const periodLabel = `This shift${
    data?.shift?.date ? ` · ${data.shift.date}` : ''
  }`;
  const emptyText = data?.shift
    ? 'No raffles on this shift yet.'
    : 'Clock in to see your raffles.';

  return (
    <div className="rf-pad rf-col rf-gap-16">
      {/* R48 — the totals head the list. */}
      <div className="rf-summary">
        <div className="rf-summary-label">{periodLabel}</div>
        <div
          className="rf-row rf-gap-16 rf-wrap"
          style={{ alignItems: 'flex-end', marginTop: 8 }}
        >
          <div>
            <div className="rf-summary-big">{money(totals.paidAmount)}</div>
            <div className="rf-summary-sub">paid out</div>
          </div>
          <div>
            <div className="rf-summary-mid">{totals.paidCount}</div>
            <div className="rf-summary-sub">raffles paid</div>
          </div>
          {totals.unclaimedCount > 0 && (
            <div>
              <div className="rf-summary-mid" style={{ color: '#ffb020' }}>
                {totals.unclaimedCount}
              </div>
              <div className="rf-summary-sub">unclaimed</div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="rf-summary-btn"
          disabled={!data?.items?.length}
          onClick={() => setReportTrigger((n) => n + 1)}
        >
          <VisibilityIcon fontSize="small" /> Preview shift report
        </button>
      </div>

      <SessionReportPdf
        type="raffle"
        userId={boot.userId}
        locationId={locationId}
        locationName={boot.locationName || locationName}
        trigger={reportTrigger}
        onError={(m) => notify('error', m)}
      />

      {loading && !data && (
        <div className="rf-center rf-pad">
          <Spinner color={SLATE} size={24} />
        </div>
      )}
      {data && data.items.length === 0 && (
        <Notice tone="slate">{emptyText}</Notice>
      )}

      <div className="rf-history-grid">
        {(data?.items || []).map((h) => (
          <HistoryCard key={h.key} h={h} onOpen={() => setDetail(h.key)} />
        ))}
      </div>

      {openItem && (
        <DetailSheet
          key={openItem.key}
          h={openItem}
          boot={boot}
          locationId={locationId}
          viewOnly={viewOnly}
          onClose={() => setDetail(null)}
          onChanged={(raffle, message) => {
            updateItem(raffle);
            notify('success', message);
            load();
          }}
        />
      )}
    </div>
  );
}

function NumberBadge({ h }) {
  if (h.status === 'carry') {
    return (
      <div className="rf-carry-badge">
        <RepeatIcon style={{ color: AMBER }} />
      </div>
    );
  }
  if (h.mode === 'ticket' && h.colorName) {
    return (
      <div
        style={{
          textDecoration: h.status === 'void' ? 'line-through' : 'none',
        }}
      >
        <TicketStub number={h.value} color={h.colorName} size="sm" />
      </div>
    );
  }
  // Auto numbers share the machine tile's shape but never its label — they
  // are raffle numbers, not machine numbers — and they are not zero-padded.
  const auto = h.mode === 'auto';
  return (
    <MachineTile
      label={auto ? 'NO.' : 'MCH'}
      value={
        h.value === null || h.value === undefined
          ? '--'
          : auto
          ? String(h.value)
          : pad(h.value)
      }
      struck={h.status === 'void'}
    />
  );
}

function StatusIcon({ h }) {
  if (h.status === 'carry') return null;
  if (h.status === 'paid')
    return (
      <Avatar name={h.customerName || ''} size={40} photo src={h.photoUrl} />
    );
  const map = {
    void: [
      'rf-dot-red',
      <BlockIcon key="v" style={{ color: RED, fontSize: 18 }} />,
    ],
    unclaimed: [
      'rf-dot-amber',
      <WarningAmberIcon key="u" style={{ color: AMBER, fontSize: 18 }} />,
    ],
    open: ['rf-dot-lav', <HourglassTopIcon key="o" style={{ fontSize: 18 }} />],
  }[h.status];
  return <div className={`rf-dot ${map[0]}`}>{map[1]}</div>;
}

function HistoryCard({ h, onOpen }) {
  const title = {
    carry: 'Pot carried over',
    paid: h.customerName,
    void: 'Voided',
    unclaimed: 'No winner claimed',
    open: 'In progress',
  }[h.status];
  return (
    <button
      type="button"
      className="rf-card rf-history"
      style={{ opacity: h.status === 'void' ? 0.6 : 1 }}
      onClick={onOpen}
    >
      <div className="rf-row rf-gap-12">
        <NumberBadge h={h} />
        <StatusIcon h={h} />
        <div className="rf-flex1 rf-minw0 rf-text-left">
          <div className="rf-strong rf-ellipsis">{title}</div>
          <div className="rf-xs rf-muted">
            {h.status === 'carry'
              ? `${money(h.potFrom)} → ${money(h.potTo)} · ${h.time}`
              : `${
                  h.status === 'void' && h.customerName
                    ? `${h.customerName} · `
                    : ''
                }${h.date} · ${h.time}`}
          </div>
          {h.status === 'carry' && h.carriedNumbers?.length > 0 && (
            <div className="rf-xs rf-muted">
              No winner on: {h.carriedNumbers.map((n) => n.value).join(', ')}
            </div>
          )}
        </div>
        <div
          className="rf-history-amount"
          style={{
            color:
              h.status === 'paid'
                ? GREEN
                : h.status === 'carry'
                ? AMBER
                : SLATE,
          }}
        >
          {h.status === 'paid'
            ? money(h.amount)
            : h.status === 'carry'
            ? `+${money((h.potTo || 0) - (h.potFrom || 0))}`
            : '—'}
        </div>
      </div>
      <div className="rf-row rf-between rf-divider-top">
        <div className="rf-row rf-gap-8">
          <span className="rf-xs rf-strong">
            {h.raffleNo ? `Raffle #${h.raffleNo}` : 'Earlier raffle'}
          </span>
          <span className="rf-badge-lav">
            {h.mode === 'machine' ? 'Machine number' : h.drawingName}
          </span>
        </div>
        <span className="rf-xs rf-muted">By {h.byName}</span>
      </div>
    </button>
  );
}

function DetailSheet({ h, boot, locationId, viewOnly, onClose, onChanged }) {
  const [mode, setMode] = useState('view'); // view | edit | void
  const [amount, setAmount] = useState(String(h.amount || ''));
  const [customer, setCustomer] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // "Number" rather than "Ticket" on an auto raffle: nothing was printed.
  const numberWord =
    h.mode === 'machine' ? 'Machine' : h.mode === 'auto' ? 'Number' : 'Ticket';
  const rows = [
    [
      'Type',
      h.mode === 'machine'
        ? 'Machine number'
        : `${h.mode === 'auto' ? 'Auto numbers' : 'Ticket'} · ${h.drawingName}`,
    ],
    ...(h.status === 'carry'
      ? [
          ['Pot', `${money(h.potFrom)} → ${money(h.potTo)}`],
          [
            'Numbers with no winner',
            (h.carriedNumbers || []).map((n) => n.value).join(', ') || '—',
          ],
        ]
      : [
          [
            numberWord,
            h.value !== null && h.value !== undefined
              ? h.mode === 'ticket'
                ? `${h.value} · ${h.colorName || ''}`
                : h.mode === 'auto'
                ? String(h.value)
                : pad(h.value)
              : '—',
          ],
          [
            'Prize',
            h.status === 'paid' || h.status === 'void'
              ? money(h.amount)
              : 'Not paid',
          ],
          ['Customer', h.customerName || '—'],
        ]),
    [
      'Status',
      {
        paid: 'Paid',
        void: `Voided · ${h.voidReason || ''}`,
        unclaimed: 'No winner claimed',
        carry: 'Pot carried over',
        open: 'In progress',
      }[h.status],
    ],
    ...(h.discarded?.length
      ? [['Numbers with no winner', h.discarded.map((d) => d.value).join(', ')]]
      : []),
    ...(h.poolText
      ? [
          [
            h.mode === 'auto' ? 'Numbers in the draw' : 'Tickets in the draw',
            h.poolText,
          ],
        ]
      : []),
    ['Recorded by', `${h.byName} · ${h.date} ${h.time}`],
    ...(h.edited
      ? [['Edited', `${h.edited.byName} · ${h.edited.date} ${h.edited.time}`]]
      : []),
    ...(h.status === 'void' && h.voidedByName
      ? [['Voided by', h.voidedByName]]
      : []),
  ];

  const saveEdit = async () => {
    setBusy(true);
    const res = await raffleApi.editRaffle({
      raffleId: h.raffleId,
      amount,
      ...(customer ? { customerId: customer.customerId } : {}),
    });
    setBusy(false);
    if (!res.success) return setError(res.message);
    onChanged(res.raffle, `Raffle #${h.raffleNo} updated.`);
    setMode('view');
  };

  const doVoid = async () => {
    setBusy(true);
    const res = await raffleApi.voidRaffle(h.raffleId, reason);
    setBusy(false);
    if (!res.success) return setError(res.message);
    onChanged(
      { ...res.raffle, status: 'void' },
      `Raffle #${h.raffleNo} voided.`
    );
    setMode('view');
  };

  if (mode === 'edit') {
    return (
      <Sheet onClose={() => setMode('view')}>
        <SheetTitle Icon={EditIcon} onClose={() => setMode('view')}>
          Edit raffle #{h.raffleNo}
        </SheetTitle>
        {/* R52 */}
        <div className="rf-muted" style={{ marginBottom: 16 }}>
          The winning number can't change — it came out of the draw. You can
          correct the prize or the customer.
        </div>
        <SectionLabel>Prize amount</SectionLabel>
        <MoneyInput big value={amount} onChange={setAmount} />
        <div style={{ margin: '16px 0 20px' }}>
          <SectionLabel>Customer</SectionLabel>
          <CustomerSwap
            locationId={locationId}
            current={h.customerName}
            value={customer}
            onChange={setCustomer}
          />
        </div>
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="red">{error}</Notice>
          </div>
        )}
        <div className="rf-row rf-gap-8">
          <button
            type="button"
            className="rf-btn rf-btn-outline rf-flex1"
            onClick={() => setMode('view')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-navy rf-flex1"
            disabled={busy || !(Number(amount) > 0)}
            onClick={saveEdit}
          >
            Save changes
          </button>
        </div>
      </Sheet>
    );
  }

  if (mode === 'void') {
    return (
      <Sheet onClose={() => setMode('view')}>
        <SheetTitle
          Icon={BlockIcon}
          iconColor={RED}
          onClose={() => setMode('view')}
        >
          Void raffle #{h.raffleNo}
        </SheetTitle>
        <div className="rf-muted" style={{ marginBottom: 16 }}>
          The raffle stays in the record crossed out and stops counting in the
          shift total. It can't be undone.
        </div>
        <SectionLabel>Reason</SectionLabel>
        <div className="rf-col rf-gap-8" style={{ marginBottom: 20 }}>
          {(boot.voidReasons || []).map((r) => (
            <button
              key={r}
              type="button"
              className="rf-radio"
              data-on={reason === r ? '1' : '0'}
              onClick={() => setReason(r)}
            >
              <span className="rf-radio-dot" />
              <span>{r}</span>
            </button>
          ))}
        </div>
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="red">{error}</Notice>
          </div>
        )}
        <div className="rf-row rf-gap-8">
          <button
            type="button"
            className="rf-btn rf-btn-outline rf-flex1"
            onClick={() => setMode('view')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-red rf-flex1"
            disabled={busy || !reason}
            onClick={doVoid}
          >
            Void raffle
          </button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet onClose={onClose}>
      <SheetTitle onClose={onClose}>
        {h.raffleNo ? `Raffle #${h.raffleNo}` : 'Earlier raffle'}
      </SheetTitle>
      {h.photoUrl && (
        <div className="rf-center" style={{ marginBottom: 12 }}>
          <img
            src={h.photoUrl}
            alt="Payout"
            className="rf-detail-photo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}
      <div className="rf-kv-box">
        {rows.map(([k, v]) => (
          <div key={k} className="rf-kv">
            <span>{k}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>

      {h.raffleId &&
        !viewOnly &&
        // R51 — only the employee who created it can change it.
        (h.canEdit ? (
          <div className="rf-row rf-gap-8">
            <button
              type="button"
              className="rf-btn rf-btn-outline rf-flex1"
              onClick={() => setMode('edit')}
            >
              <EditIcon fontSize="small" /> Edit
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-redsoft rf-flex1"
              onClick={() => setMode('void')}
            >
              <BlockIcon fontSize="small" /> Void
            </button>
          </div>
        ) : (
          <Notice Icon={LockIcon}>
            {h.status === 'void'
              ? "This raffle is voided and can't change."
              : h.legacy
              ? 'Raffles from before this screen are read only here.'
              : `Only ${h.byName} can edit this raffle.`}
          </Notice>
        ))}

      {h.raffleId && (
        <button
          type="button"
          className="rf-btn rf-btn-navy rf-w100"
          style={{ marginTop: 8 }}
          onClick={() => downloadReceipt(h, boot.locationName)}
        >
          <DownloadIcon fontSize="small" /> Download this receipt
        </button>
      )}
    </Sheet>
  );
}

function CustomerSwap({ locationId, current, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const res = await raffleApi.searchCustomers(locationId, query.trim());
      setResults(res.success ? res.customers : []);
    }, 400);
    return () => clearTimeout(t);
  }, [query, locationId]);

  if (!open) {
    return (
      <button
        type="button"
        className="rf-btn rf-btn-outline rf-w100"
        onClick={() => setOpen(true)}
      >
        <HowToRegIcon fontSize="small" />{' '}
        {value ? fullName(value) : current || 'Change customer'} · Change
      </button>
    );
  }
  return (
    <div className="rf-col rf-gap-8">
      <div className="rf-search">
        <SearchIcon style={{ color: SLATE }} />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, phone or customer ID"
        />
      </div>
      {results.map((c) => (
        <button
          key={c.customerId}
          type="button"
          className="rf-card rf-pick"
          onClick={() => {
            onChange(c);
            setOpen(false);
          }}
        >
          <Avatar name={fullName(c)} size={36} src={c.photoUrl} />
          <div className="rf-flex1 rf-text-left">
            <div className="rf-strong">{fullName(c)}</div>
            <div className="rf-xs rf-muted">{formatPhone(c.phone)}</div>
          </div>
        </button>
      ))}
    </div>
  );
}
